import Button from "./Button";
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  TableChart as ExcelIcon,
  InsertDriveFile as FileIcon,
  FolderZip as ZipIcon,
  Replay as ReuseIcon,
  AutoFixHigh as CreateIcon,
  Download as DownloadIcon,
  Link as LinkIcon,
  AutoAwesome as TriageSparkleIcon,
  FolderOpen as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  Stack,
  Typography,
  Chip,
  Tabs,
  Tab,
  TextField,
  Tooltip,
  Paper,
  ListItemButton,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { sanitizeHtml } from "common/html";
import * as logger from "common/logger";
import InlineSearch from "components/common/InlineSearch";
import SubmissionReviewDialog from "components/common/SubmissionReviewDialog";
import TriageResultDialog from "components/common/TriageResultDialog";
import TriageRoutingIcon from "components/common/TriageRoutingIcon";
import { FIELD_LABELS as REVIEW_FIELD_LABELS } from "components/common/submissionReviewFields";
import {
  captureDroppedEntries,
  createFileEntry,
  matchFileByName,
  processDroppedItems,
  triggerBlobDownload,
} from "fileUtils";
import {
  SUBMISSION_FILE_UPLOAD_DRAWER,
  SUBMISSION_TRIAGE,
  SHOW_AI_FILE_SELECTION_LABELS,
  AI_CARRY_FORWARD_PAGE,
  AI_CARRY_FORWARD_CONFIDENCE,
} from "flags";
import { useSnackbar } from "notistack";
import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  aiAgentsApi,
  useRegisterIngestionFileMutation,
  useGetIngestionFilesQuery,
  useSetFileReuseMutation,
  useGetAvailableIngestionDataQuery,
  useMarkFileAsUsedMutation,
  useRerunIngestionFileMutation,
  useIngestionFileUrlMutation,
  useRunSubmissionTriageMutation,
} from "services/aiAgents";
import { claimsApi } from "services/claimsService";
import { filesApi } from "services/filesService";
import * as staticDataService from "services/staticDataService";
import * as submissionsService from "services/submissionsService";
import { useRetrievePreferencesQuery } from "services/userService";
import * as programActions from "store/actions/input/program/programActions";
import * as persistenceActions from "store/actions/persistence/persistenceActions";
import * as persistenceSelectors from "store/selectors/persistence/persistenceSelectors";
import { selectCurrencyCodes } from "store/selectors/temp/staticData/staticDataSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";
import {
  recordSuggestionSelection,
  recordFieldValue,
  buildFieldUsageByFile,
} from "utils/aiSuggestionUsageTracker";
import { getSuggestionsConfig } from "utils/aiSuggestionsConfig";
import {
  EMAIL_BODY_FILENAMES,
  FILE_URL_EXPIRY_SECONDS,
  SUPPORTED_EXTENSIONS,
  downloadIngestionSource,
  formatFileSize,
  isReadableIngestionFile,
} from "utils/ingestionFiles";
import {
  extractBestFields,
  SUGGESTION_FIELDS,
} from "utils/ingestionSuggestions";

const getFileIcon = (fileName) => {
  const ext = fileName.split(".").pop().toLowerCase();
  switch (ext) {
    case "pdf":
      return <PdfIcon sx={{ fontSize: 40, color: "#d32f2f" }} />;
    case "docx":
      return <DocIcon sx={{ fontSize: 40, color: "#1976d2" }} />;
    case "xls":
    case "xlsx":
    case "xlsm":
    case "csv":
    case "tsv":
      return <ExcelIcon sx={{ fontSize: 40, color: "#2e7d32" }} />;
    case "zip":
      return <ZipIcon sx={{ fontSize: 40, color: "#7b1fa2" }} />;
    default:
      return <FileIcon sx={{ fontSize: 40, color: "text.secondary" }} />;
  }
};

const FileListItem = ({ file, status, error, progress }) => {
  const getStatusChip = () => {
    if (status === "uploading") {
      return (
        <Chip
          label={`Uploading ${progress}%`}
          size={"small"}
          color={"primary"}
          variant={"outlined"}
        />
      );
    }
    if (status === "processing") {
      return (
        <Chip
          label={"Processing"}
          size={"small"}
          color={"info"}
          variant={"outlined"}
        />
      );
    }
    if (status === "success") {
      return (
        <Chip
          label={"Complete"}
          size={"small"}
          color={"success"}
          icon={<SuccessIcon />}
        />
      );
    }
    if (status === "error") {
      return (
        <Chip
          label={"Failed"}
          size={"small"}
          color={"error"}
          icon={<ErrorIcon />}
        />
      );
    }
    return <Chip label={"Queued"} size={"small"} variant={"outlined"} />;
  };

  const showProgress = status === "uploading" || status === "processing";

  return (
    <ListItem
      sx={{
        flexDirection: "column",
        alignItems: "stretch",
        border: "1px solid",
        borderColor: status === "error" ? "error.main" : "divider",
        borderRadius: 2,
        mb: 1.5,
        p: 2,
        backgroundColor: "background.paper",
        "&:hover": {
          backgroundColor: "action.hover",
        },
      }}
    >
      <Stack
        direction={"row"}
        spacing={2}
        alignItems={"center"}
        mb={showProgress ? 1 : 0}
      >
        <Box sx={{ flexShrink: 0 }}>{getFileIcon(file.name)}</Box>
        <Box flex={1} minWidth={0}>
          <Tooltip title={file.name} arrow>
            <Typography
              variant={"body2"}
              fontWeight={500}
              noWrap
              sx={{ mb: 0.5 }}
            >
              {file.name}
            </Typography>
          </Tooltip>
          <Typography variant={"caption"} color={"text.secondary"}>
            {formatFileSize(file.size)}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0 }}>{getStatusChip()}</Box>
      </Stack>

      {showProgress && (
        <LinearProgress
          variant={status === "uploading" ? "determinate" : "indeterminate"}
          value={status === "uploading" ? progress : undefined}
          sx={{
            height: 6,
            borderRadius: 3,
            backgroundColor: "action.hover",
          }}
        />
      )}

      {error && (
        <Typography
          variant={"caption"}
          color={"error"}
          sx={{ mt: 1, wordBreak: "break-word" }}
        >
          {error}
        </Typography>
      )}
    </ListItem>
  );
};

const AttachSubmissionSearch = ({ open, onClose, onSelect, fileEntry }) => {
  const [searchText, setSearchText] = useState("");
  const {
    data: submissionsData,
  } = submissionsService.useListSubmissionsQuery(undefined, { skip: !open });

  if (!open) return null;

  const submissions = submissionsData || [];
  const filteredSubmissions = submissions
    .filter((sub) => {
      if (!searchText) return true;
      const searchLower = searchText.toLowerCase();
      return (
        sub.insured?.toLowerCase().includes(searchLower) ||
        sub.submissionId?.toLowerCase().includes(searchLower) ||
        sub.reference?.toLowerCase().includes(searchLower)
      );
    })
    .slice(0, 8);

  return (
    <Paper
      elevation={8}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        borderRadius: 2,
        maxHeight: "400px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{ p: 2, pb: 1 }}
      >
        <Typography variant={"subtitle2"}>{"Attach to submission"}</Typography>
        <IconButton size={"small"} onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Box sx={{ px: 2, pb: 1 }}>
        <InlineSearch
          placeholder={"Search by insured, reference, ID..."}
          onChange={setSearchText}
          autoFocus
          fullWidth
        />
      </Box>
      <Divider />
      <List sx={{ overflow: "auto", py: 0, maxHeight: "300px" }}>
        {filteredSubmissions.length === 0 ? (
          <Typography
            variant={"body2"}
            color={"text.secondary"}
            sx={{ p: 2, textAlign: "center" }}
          >
            {searchText
              ? `No submissions found for "${searchText}"`
              : "No submissions available"}
          </Typography>
        ) : (
          filteredSubmissions.map((submission) => (
            <ListItemButton
              key={submission.submissionId}
              onClick={() => {
                onSelect(submission);
                onClose();
                setSearchText("");
              }}
            >
              <ListItemText
                primary={submission.insured || "Untitled"}
                secondary={`${submission.inception || "No inception"} - ${
                  submission.reference || "No reference"
                }`}
              />
            </ListItemButton>
          ))
        )}
      </List>
    </Paper>
  );
};

const FolderNamingDialog = ({
  open,
  suggestedName,
  fileCount,
  onConfirm,
  onSkip,
}) => {
  const [name, setName] = useState(suggestedName || "");
  useEffect(() => {
    setName(suggestedName || "");
  }, [suggestedName]);
  return (
    <Dialog open={open} maxWidth={"xs"} fullWidth>
      <DialogTitle>
        <Stack direction={"row"} alignItems={"center"} spacing={1}>
          <FolderIcon color={"primary"} />
          <Typography variant={"h6"}>{"Name this project"}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Typography variant={"body2"} color={"text.secondary"} sx={{ mb: 2 }}>
          {`You're uploading ${fileCount} file${
            fileCount !== 1 ? "s" : ""
          }. Give this group a project name to find it easily later.`}
        </Typography>
        <TextField
          label={"Project name"}
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size={"small"}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim()) onConfirm(name.trim());
          }}
          placeholder={"e.g. Q1 2025 Renewal - Acme Corp"}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color={"secondary"} onClick={onSkip}>
          {"Upload without folder"}
        </Button>
        <Button
          color={"primary"}
          onClick={() => onConfirm(name.trim() || suggestedName || "Project")}
          disabled={!name.trim()}
        >
          {"Create project"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// The headline number for a triaged row: the weighted score, which is what the pipeline
// table and the report drawer both lead with. Falls back to the pass fraction only when
// the ruleset's weights say nothing, where a percentage would restate the fraction.
export const triageHeadline = (report) => {
  if (!report) return null;
  if (report.weights_informative && typeof report.weighted_score === "number") {
    return {
      pct: Math.round(report.weighted_score),
      label: `${report.weighted_score}%`,
      tooltip: `${report.weighted_score}% weighted · ${report.passed_count}/${report.total_count} rules passed`,
    };
  }
  const total = report.total_count || 0;
  return {
    pct: total > 0 ? Math.round((report.passed_count / total) * 100) : 0,
    label: `${report.passed_count}/${total}`,
    tooltip: `${report.passed_count}/${total} rules passed`,
  };
};

const FolderGroup = ({
  group,
  source,
  usedFiles = [],
  skippedFiles = [],
  ...fileItemProps
}) => {
  const [expanded, setExpanded] = useState(true);
  const [isHoveringStatus, setIsHoveringStatus] = useState(false);
  const [folderAttachDialogOpen, setFolderAttachDialogOpen] = useState(false);
  const [emailBodyText, setEmailBodyText] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const dispatch = useDispatch();

  const isEmailSource = source === "email";
  const emailBodyFile = isEmailSource
    ? group.files.find((f) => EMAIL_BODY_FILENAMES.has(f.file_name))
    : null;
  const emailBodyIsHtml = emailBodyFile?.file_name === "email_body.html";
  const attachmentFiles = emailBodyFile
    ? group.files.filter((f) => f.file_id !== emailBodyFile.file_id)
    : group.files;

  // The first READABLE file, not simply the first: a folder built from a zip lists the
  // archive first, and triaging the representative then sent the archive on its own.
  const repFile =
    attachmentFiles.find((f) => isReadableIngestionFile(f.file_name)) ||
    attachmentFiles[0] ||
    group.files[0];

  const { submissionDataById, onToggleReuse, isReuseUpdating } = fileItemProps;

  const handleOpenEmailDialog = useCallback(
    async (e) => {
      e.stopPropagation();
      setEmailBodyText(null); // show loading state
      setEmailDialogOpen(true);
      try {
        const result = await dispatch(
          // forceRefetch so we never serve a cached expired presigned URL
          filesApi.endpoints.fileUrl.initiate(emailBodyFile.file_id, {
            forceRefetch: true,
          })
        ).unwrap();
        const response = await fetch(result.url);
        const text = await response.text();
        setEmailBodyText(emailBodyIsHtml ? sanitizeHtml(text) : text);
      } catch {
        setEmailBodyText("");
      }
    },
    [dispatch, emailBodyFile, emailBodyIsHtml]
  );

  const folderSavedSubmissionIds = [
    ...new Set(group.files.flatMap((f) => f.submission_ids || []).map(String)),
  ].filter((id) => submissionDataById?.get(id)?.insured);

  const processableFiles = group.files.filter(
    (f) => f.process_state !== "unsupported"
  );
  const allComplete =
    processableFiles.length > 0 &&
    processableFiles.every((f) => f.process_state === "complete");
  const anyFailed = processableFiles.some((f) => f.process_state === "failed");
  const anyPending = processableFiles.some((f) =>
    ["pending", "not_started", "pending_folder"].includes(f.process_state)
  );
  const statusColor = anyFailed
    ? "error"
    : anyPending
    ? "info"
    : allComplete
    ? "success"
    : "default";
  const statusLabel = anyFailed
    ? isHoveringStatus
      ? "Rerun"
      : "Has failures"
    : anyPending
    ? "Processing"
    : allComplete
    ? isHoveringStatus
      ? "Rerun"
      : "Complete"
    : "Mixed";

  const triageResult = repFile ? fileItemProps.triageResult(repFile) : null;
  const triageLoading = repFile ? fileItemProps.triageLoading(repFile) : false;
  const triageError = repFile ? fileItemProps.triageError(repFile) : null;
  const triageDialogOpen = repFile
    ? fileItemProps.triageDialogOpen(repFile)
    : false;

  const canRerun = allComplete || anyFailed;
  const canCreateSubmission =
    allComplete && repFile && (!repFile.used_in_submission || repFile.reuse);
  const createFromFileEnabled =
    fileItemProps.suggestionsConfig?.CREATE_SUBMISSION_FROM_FILE;

  const headline = triageHeadline(triageResult?.report);
  const triagePct = headline ? headline.pct : null;
  const barColor =
    triagePct === 100 ? "success" : triagePct >= 60 ? "warning" : "error";

  return (
    <Box sx={{ mb: 1.5, position: "relative" }}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        {/* Header row — toggle expand */}
        <ListItemButton
          onClick={() => setExpanded((v) => !v)}
          sx={{ px: 2, py: 1.25 }}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
            flex={1}
            minWidth={0}
          >
            <FolderIcon
              color={"primary"}
              sx={{ fontSize: 20, flexShrink: 0 }}
            />
            <Box flex={1} minWidth={0}>
              <Typography variant={"body2"} fontWeight={600} noWrap>
                {group.folder_name}
              </Typography>
              <Typography variant={"caption"} color={"text.secondary"}>{`${
                attachmentFiles.length
              } file${attachmentFiles.length !== 1 ? "s" : ""}`}</Typography>
            </Box>
          </Stack>
          {expanded ? (
            <ExpandLessIcon
              sx={{ fontSize: 18, color: "text.secondary", ml: 1 }}
            />
          ) : (
            <ExpandMoreIcon
              sx={{ fontSize: 18, color: "text.secondary", ml: 1 }}
            />
          )}
        </ListItemButton>

        {/* Action row — folder-level actions, always visible */}
        <Box
          sx={{
            px: 2,
            pb: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {/* Status chip with hover-rerun */}
          <Chip
            label={statusLabel}
            size={"small"}
            color={
              isHoveringStatus && canRerun
                ? anyFailed
                  ? "warning"
                  : "primary"
                : statusColor
            }
            icon={
              isHoveringStatus && canRerun ? (
                <ReuseIcon />
              ) : allComplete ? (
                <SuccessIcon />
              ) : anyFailed ? (
                <ErrorIcon />
              ) : undefined
            }
            variant={allComplete && !isHoveringStatus ? "filled" : "outlined"}
            sx={{
              fontSize: "0.68rem",
              cursor: canRerun ? "pointer" : "default",
            }}
            onMouseEnter={() => canRerun && setIsHoveringStatus(true)}
            onMouseLeave={() => setIsHoveringStatus(false)}
            onClick={
              canRerun && isHoveringStatus
                ? () => repFile && fileItemProps.onRerun(repFile)
                : undefined
            }
          />

          {/* Email message preview */}
          {isEmailSource && emailBodyFile && (
            <Tooltip title={"View email message"}>
              <IconButton
                size={"small"}
                onClick={handleOpenEmailDialog}
                sx={{ p: 0.5 }}
              >
                <EmailIcon sx={{ fontSize: 16, color: "primary.main" }} />
              </IconButton>
            </Tooltip>
          )}

          <Box flex={1} />

          {/* Reuse */}
          {repFile?.used_in_submission && (
            <Tooltip title={repFile.reuse ? "Reuse enabled" : "Reuse"}>
              <IconButton
                size={"small"}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleReuse(repFile);
                }}
                disabled={isReuseUpdating}
                sx={{ p: 0.5 }}
              >
                <ReuseIcon
                  color={repFile.reuse ? "disabled" : "primary"}
                  sx={repFile.reuse ? { opacity: 0.6 } : undefined}
                />
              </IconButton>
            </Tooltip>
          )}

          {/* Attach to existing submission */}
          {repFile && (
            <Tooltip title={"Attach folder to existing submission"}>
              <IconButton
                size={"small"}
                onClick={(e) => {
                  e.stopPropagation();
                  setFolderAttachDialogOpen(true);
                }}
                sx={{ p: 0.5 }}
              >
                <LinkIcon sx={{ fontSize: 16, color: "primary.main" }} />
              </IconButton>
            </Tooltip>
          )}

          {/* Create submission from folder */}
          {createFromFileEnabled && repFile && (
            <Tooltip title={"Create submission from project"}>
              <span>
                <IconButton
                  size={"small"}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileItemProps.onCreateSubmission(
                      repFile,
                      group.folder_name,
                      group.files,
                      group.folder_id
                    );
                  }}
                  disabled={
                    fileItemProps.isCreatingSubmission || !canCreateSubmission
                  }
                  color={"primary"}
                  sx={{ p: 0.5 }}
                >
                  <CreateIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Run triage */}
          {fileItemProps.triageEnabled && repFile && !triageResult && (
            <Tooltip
              title={triageError ? "Retry triage" : "Run triage on project"}
            >
              <span>
                <IconButton
                  size={"small"}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileItemProps.onRunTriage(repFile);
                  }}
                  disabled={triageLoading || !allComplete}
                  color={triageError ? "warning" : "primary"}
                  sx={{ p: 0.5 }}
                >
                  <Box sx={{ position: "relative", display: "flex" }}>
                    <TriageSparkleIcon
                      sx={{
                        fontSize: "0.55rem",
                        position: "absolute",
                        top: -1,
                        left: -2,
                        color: "warning.main",
                      }}
                    />
                    <TriageRoutingIcon fontSize={"small"} />
                  </Box>
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Triage score output */}
          {triageResult && (
            <Tooltip title={`View triage report · ${headline.tooltip}`} arrow>
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  fileItemProps.onOpenTriageDialog(repFile, true);
                }}
                sx={{
                  width: 48,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.25,
                }}
              >
                <Typography
                  variant={"caption"}
                  sx={{
                    fontSize: "0.6rem",
                    lineHeight: 1,
                    color: `${barColor}.main`,
                    fontWeight: 700,
                  }}
                >
                  {headline.label}
                </Typography>
                <LinearProgress
                  variant={"determinate"}
                  value={triagePct}
                  color={barColor}
                  sx={{
                    width: "100%",
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Box>

        {triageDialogOpen && repFile && (
          <TriageResultDialog
            open={triageDialogOpen}
            onClose={() => fileItemProps.onOpenTriageDialog(repFile, false)}
            result={triageResult}
            fileId={repFile.file_id}
            fileName={group.folder_name}
          />
        )}

        {folderSavedSubmissionIds.length > 0 && (
          <Box sx={{ px: 2, pb: 1.25 }}>
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ mb: 0.5, display: "block" }}
            >
              {folderSavedSubmissionIds.length === 1
                ? "Used in submission:"
                : "Used in submissions:"}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {folderSavedSubmissionIds.map((id) =>
                renderSubmissionChip(id, submissionDataById)
              )}
            </Box>
          </Box>
        )}

        {/* Email body dialog */}
        {isEmailSource && emailBodyFile && (
          <Dialog
            open={emailDialogOpen}
            onClose={() => setEmailDialogOpen(false)}
            maxWidth={"md"}
            fullWidth
            onClick={(e) => e.stopPropagation()}
          >
            <DialogTitle>
              <Stack direction={"row"} alignItems={"center"} spacing={1}>
                <EmailIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <Typography variant={"subtitle1"} fontWeight={600}>
                  {group.folder_name}
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, minHeight: 300 }}>
              {emailBodyText === null ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant={"body2"} color={"text.secondary"}>
                    {"Loading…"}
                  </Typography>
                </Box>
              ) : emailBodyIsHtml ? (
                <iframe
                  srcDoc={emailBodyText}
                  sandbox={""}
                  title={"Email body"}
                  style={{
                    width: "100%",
                    minHeight: 400,
                    border: "none",
                    display: "block",
                  }}
                />
              ) : (
                <Box sx={{ p: 3 }}>
                  <Typography
                    variant={"body2"}
                    component={"pre"}
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontFamily: "inherit",
                      m: 0,
                    }}
                  >
                    {emailBodyText}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                color={"secondary"}
                onClick={() => setEmailDialogOpen(false)}
              >
                {"Close"}
              </Button>
            </DialogActions>
          </Dialog>
        )}

        <Collapse in={expanded}>
          <Divider />
          <Box sx={{ pl: 2 }}>
            {attachmentFiles.map((fileEntry) => {
              const hasSkipped = skippedFiles.length > 0;
              const aiLabel = hasSkipped
                ? usedFiles.includes(fileEntry.file_name)
                  ? "used"
                  : skippedFiles.includes(fileEntry.file_name)
                  ? "skipped"
                  : null
                : null;
              return (
                <UploadedFileItem
                  key={fileEntry.file_id}
                  fileEntry={fileEntry}
                  isInFolder
                  {...fileItemProps}
                  triageResult={fileItemProps.triageResult(fileEntry)}
                  triageLoading={fileItemProps.triageLoading(fileEntry)}
                  triageError={fileItemProps.triageError(fileEntry)}
                  triageDialogOpen={false}
                  onOpenTriageDialog={() => {}}
                  onRerun={() => fileItemProps.onRerun(fileEntry)}
                  aiLabel={aiLabel}
                />
              );
            })}
          </Box>
        </Collapse>
      </Box>
      {/* Submission search overlay — positioned relative to the outer wrapper */}
      <AttachSubmissionSearch
        open={folderAttachDialogOpen}
        onClose={() => setFolderAttachDialogOpen(false)}
        fileEntry={repFile}
        onSelect={(submission) => {
          fileItemProps.onAttachToSubmission(
            repFile,
            submission.submissionId,
            submission.insured,
            group.files,
            group.folder_name
          );
        }}
      />
    </Box>
  );
};

const UploadFolderGroup = ({
  folderName,
  entries,
  fileStatuses,
  statusColor,
  statusLabel,
  allDone,
}) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <Box
      sx={{
        mb: 1.5,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "background.paper",
      }}
    >
      <ListItemButton
        onClick={() => setExpanded((v) => !v)}
        sx={{ px: 2, py: 1.25 }}
      >
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          flex={1}
          minWidth={0}
        >
          <FolderIcon color={"primary"} sx={{ fontSize: 20, flexShrink: 0 }} />
          <Box flex={1} minWidth={0}>
            <Typography variant={"body2"} fontWeight={600} noWrap>
              {folderName}
            </Typography>
            <Typography variant={"caption"} color={"text.secondary"}>{`${
              entries.length
            } file${entries.length !== 1 ? "s" : ""}`}</Typography>
          </Box>
          <Chip
            label={statusLabel}
            size={"small"}
            color={statusColor}
            variant={allDone ? "filled" : "outlined"}
            sx={{ fontSize: "0.68rem" }}
          />
        </Stack>
        {expanded ? (
          <ExpandLessIcon
            sx={{ fontSize: 18, color: "text.secondary", ml: 1 }}
          />
        ) : (
          <ExpandMoreIcon
            sx={{ fontSize: 18, color: "text.secondary", ml: 1 }}
          />
        )}
      </ListItemButton>
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ pl: 2 }}>
          {entries.map(({ id, file }) => (
            <FileListItem
              key={id}
              file={file}
              status={fileStatuses[id]?.status || "pending"}
              error={fileStatuses[id]?.error}
              progress={fileStatuses[id]?.progress || 0}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

const renderSubmissionChip = (submissionId, submissionDataById) => {
  const submissionData = submissionDataById.get(String(submissionId));
  const inceptionDate = submissionData?.inceptionDate
    ? new Date(submissionData.inceptionDate).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      })
    : "";
  const chipLabel = inceptionDate
    ? `${submissionData?.insured || submissionId} - ${inceptionDate}`
    : submissionData?.insured || submissionId;
  const tooltipTitle = inceptionDate
    ? `${submissionData?.insured || ""} - Inception: ${inceptionDate}`
    : submissionData?.insured || "";
  return (
    <Link
      key={submissionId}
      to={`/submissions/${submissionId}/inputs/program`}
      style={{ textDecoration: "none" }}
    >
      <Tooltip title={tooltipTitle} arrow>
        <Chip label={chipLabel} size={"small"} variant={"outlined"} />
      </Tooltip>
    </Link>
  );
};

const UploadedFileItem = ({
  fileEntry,
  submissionDataById,
  onToggleReuse,
  isReuseUpdating,
  onCreateSubmission,
  isCreatingSubmission,
  suggestionsConfig,
  onDownloadFile,
  onAttachToSubmission,
  onRerun,
  onRunTriage,
  triageResult,
  triageLoading,
  triageError,
  triageEnabled,
  triageDialogOpen,
  onOpenTriageDialog,
  isInFolder = false,
  aiLabel = null,
}) => {
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [isHoveringStatus, setIsHoveringStatus] = useState(false);
  const createFromFileEnabled = suggestionsConfig.CREATE_SUBMISSION_FROM_FILE;

  const getProcessStateChip = () => {
    const chipProps = {
      size: "small",
      onMouseEnter: () => setIsHoveringStatus(true),
      onMouseLeave: () => setIsHoveringStatus(false),
      onClick: isHoveringStatus ? onRerun : undefined,
      sx: {
        cursor: isHoveringStatus ? "pointer" : "default",
        minWidth: "110px",
      },
    };

    switch (fileEntry.process_state) {
      case "complete":
        return (
          <Chip
            label={isHoveringStatus ? "Rerun" : "Processed"}
            color={isHoveringStatus ? "primary" : "success"}
            icon={isHoveringStatus ? <ReuseIcon /> : <SuccessIcon />}
            {...chipProps}
          />
        );
      case "pending":
        return (
          <Chip
            label={"Processing"}
            color={"info"}
            variant={"outlined"}
            {...chipProps}
          />
        );
      case "failed":
        return (
          <Chip
            label={isHoveringStatus ? "Rerun" : "Failed"}
            color={isHoveringStatus ? "warning" : "error"}
            icon={isHoveringStatus ? <ReuseIcon /> : <ErrorIcon />}
            {...chipProps}
          />
        );
      case "not_started":
        return <Chip label={"Queued"} variant={"outlined"} {...chipProps} />;
      case "unsupported":
        return (
          <Chip
            label={"Unsupported File - Skipped"}
            color={"default"}
            variant={"outlined"}
            {...chipProps}
          />
        );
      default:
        return null;
    }
  };

  const canCreateSubmission =
    fileEntry.process_state === "complete" &&
    (!fileEntry.used_in_submission || fileEntry.reuse);

  return (
    <ListItem
      sx={{
        flexDirection: "column",
        alignItems: "stretch",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        mb: 1.5,
        p: 2,
        backgroundColor: "background.paper",
        position: "relative",
      }}
    >
      <Stack direction={"row"} spacing={2} alignItems={"center"}>
        <Box sx={{ flexShrink: 0 }}>{getFileIcon(fileEntry.file_name)}</Box>
        <Box flex={1} minWidth={0}>
          <Tooltip title={fileEntry.file_name} arrow>
            <Typography
              variant={"body2"}
              fontWeight={500}
              noWrap
              sx={{ mb: 0.5 }}
            >
              {fileEntry.file_name}
            </Typography>
          </Tooltip>
          <Stack
            direction={"row"}
            spacing={0.5}
            flexWrap={"nowrap"}
            alignItems={"center"}
          >
            <Typography variant={"caption"} color={"text.secondary"} noWrap>
              {formatFileSize(fileEntry.file_size)}
            </Typography>
            {fileEntry.uploaded_at && (
              <>
                <Typography variant={"caption"} color={"text.secondary"}>
                  {"•"}
                </Typography>
                <Tooltip
                  title={new Date(fileEntry.uploaded_at).toLocaleString()}
                  arrow
                >
                  <Typography
                    variant={"caption"}
                    color={"text.secondary"}
                    noWrap
                  >
                    {new Date(fileEntry.uploaded_at).toLocaleDateString()}
                  </Typography>
                </Tooltip>
              </>
            )}
            <IconButton
              size={"small"}
              onClick={() =>
                onDownloadFile(fileEntry.file_id, fileEntry.file_name)
              }
              sx={{ p: 0, ml: 0.25 }}
            >
              <DownloadIcon sx={{ fontSize: 16, color: "text.secondary" }} />
            </IconButton>
            {!isInFolder && (
              <Tooltip title={"Attach to existing submission"}>
                <IconButton
                  size={"small"}
                  onClick={() => setAttachDialogOpen(true)}
                  sx={{ p: 0 }}
                >
                  <LinkIcon sx={{ fontSize: 16, color: "primary.main" }} />
                </IconButton>
              </Tooltip>
            )}
            {aiLabel === "used" && (
              <Chip
                label={"Read by AI"}
                size={"small"}
                color={"success"}
                variant={"outlined"}
                sx={{
                  fontSize: "0.6rem",
                  height: 16,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}
            {aiLabel === "skipped" && (
              <Tooltip
                title={
                  "Excluded by the AI pre-selector as unlikely to contain submission data"
                }
                arrow
              >
                <Chip
                  label={"Skipped"}
                  size={"small"}
                  color={"default"}
                  variant={"outlined"}
                  sx={{
                    fontSize: "0.6rem",
                    height: 16,
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        </Box>
        {!isInFolder && (
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <Stack direction={"column"} spacing={0.5} alignItems={"center"}>
              {createFromFileEnabled && (
                <Tooltip title={"Create submission from file"}>
                  <IconButton
                    size={"small"}
                    onClick={() => onCreateSubmission(fileEntry)}
                    disabled={isCreatingSubmission || !canCreateSubmission}
                    color={"primary"}
                  >
                    <CreateIcon />
                  </IconButton>
                </Tooltip>
              )}
              {fileEntry.used_in_submission && (
                <Tooltip title={fileEntry.reuse ? "Reuse enabled" : "Reuse"}>
                  <IconButton
                    size={"small"}
                    onClick={() => onToggleReuse(fileEntry)}
                    disabled={isReuseUpdating}
                  >
                    <ReuseIcon
                      color={fileEntry.reuse ? "disabled" : "primary"}
                      sx={fileEntry.reuse ? { opacity: 0.6 } : undefined}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack direction={"column"} spacing={0.5} alignItems={"center"}>
              {triageEnabled && (
                <>
                  <Tooltip
                    title={
                      triageError
                        ? "Retry triage (result may be cached)"
                        : "Run triage"
                    }
                  >
                    <span>
                      <IconButton
                        size={"small"}
                        onClick={() => onRunTriage(fileEntry)}
                        disabled={
                          triageLoading ||
                          fileEntry.process_state !== "complete"
                        }
                        color={triageError ? "warning" : "primary"}
                      >
                        <Box sx={{ position: "relative", display: "flex" }}>
                          <TriageSparkleIcon
                            sx={{
                              fontSize: "0.55rem",
                              position: "absolute",
                              top: -1,
                              left: -2,
                              color: "warning.main",
                            }}
                          />
                          <TriageRoutingIcon fontSize={"small"} />
                        </Box>
                      </IconButton>
                    </span>
                  </Tooltip>
                  {triageResult &&
                    (() => {
                      const headline = triageHeadline(triageResult.report);
                      const pct = headline.pct;
                      const barColor =
                        pct === 100
                          ? "success"
                          : pct >= 60
                          ? "warning"
                          : "error";
                      return (
                        <Tooltip
                          title={`View triage report · ${headline.tooltip}`}
                          arrow
                        >
                          <Box
                            onClick={() => onOpenTriageDialog()}
                            sx={{
                              width: 48,
                              cursor: "pointer",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 0.25,
                            }}
                          >
                            <Typography
                              variant={"caption"}
                              sx={{
                                fontSize: "0.6rem",
                                lineHeight: 1,
                                color: `${barColor}.main`,
                                fontWeight: 700,
                              }}
                            >
                              {headline.label}
                            </Typography>
                            <LinearProgress
                              variant={"determinate"}
                              value={pct}
                              color={barColor}
                              sx={{
                                width: "100%",
                                height: 4,
                                borderRadius: 2,
                                bgcolor: "action.hover",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      );
                    })()}
                  {triageError && !triageResult && null}
                </>
              )}
            </Stack>
            {getProcessStateChip()}
          </Stack>
        )}
      </Stack>

      <TriageResultDialog
        open={triageDialogOpen}
        onClose={() => onOpenTriageDialog(false)}
        result={triageResult}
        fileId={fileEntry.file_id}
        fileName={fileEntry.file_name}
      />

      {fileEntry.last_error && (
        <Tooltip title={fileEntry.last_error} arrow>
          <Typography
            variant={"caption"}
            color={"error"}
            sx={{ mt: 1, wordBreak: "break-word" }}
            noWrap
          >
            {`Error: ${fileEntry.last_error}`}
          </Typography>
        </Tooltip>
      )}

      {!isInFolder &&
        (() => {
          const savedIds = (fileEntry.submission_ids || []).filter(
            (id) => submissionDataById.get(String(id))?.insured
          );
          if (!savedIds.length) return null;
          return (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                sx={{ mb: 0.5 }}
              >
                {savedIds.length === 1
                  ? "Used in submission:"
                  : "Used in submissions:"}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  alignItems: "center",
                }}
              >
                {savedIds.map((submissionId) =>
                  renderSubmissionChip(submissionId, submissionDataById)
                )}
              </Box>
            </Box>
          );
        })()}

      <AttachSubmissionSearch
        open={attachDialogOpen}
        onClose={() => setAttachDialogOpen(false)}
        fileEntry={fileEntry}
        onSelect={(submission) => {
          onAttachToSubmission(
            fileEntry,
            submission.submissionId,
            submission.insured
          );
        }}
      />
    </ListItem>
  );
};

const SubmissionFileUploadDrawer = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [fileStatuses, setFileStatuses] = useState({});
  const [registerIngestionFile] = useRegisterIngestionFileMutation();
  const [
    setFileReuse,
    { isLoading: isReuseUpdating },
  ] = useSetFileReuseMutation();
  const [markFileAsUsed] = useMarkFileAsUsedMutation();
  const [rerunIngestionFile] = useRerunIngestionFileMutation();
  const [runSubmissionTriage] = useRunSubmissionTriageMutation();
  const [ingestionFileUrl] = useIngestionFileUrlMutation();
  const [activeTab, setActiveTab] = useState(0);
  const [triageResults, setTriageResults] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem("triageResults") || "{}");
    } catch {
      return {};
    }
  });
  const [triageErrors, setTriageErrors] = useState({});
  const [triageLoadingIds, setTriageLoadingIds] = useState(new Set());
  const [triageOpenFileId, setTriageOpenFileId] = useState(null);
  const [selectedRulesetId, setSelectedRulesetId] = useState(null);
  const { data: userPreferences } = useRetrievePreferencesQuery();
  const triageRulesets = useMemo(
    () =>
      userPreferences?.triageRulesets || userPreferences?.triage_rulesets || [],
    [userPreferences]
  );
  const defaultTriageRuleset =
    triageRulesets.find((r) => r.isDefault) || triageRulesets[0] || null;
  const [submissionDataById, setSubmissionDataById] = useState(new Map());
  const [fetchSubmission] = submissionsService.useLazyRetrieveSubmissionQuery();
  const [patchSubmission] = submissionsService.usePatchSubmissionMutation();
  const store = useStore();
  const [isCreatingSubmission, setIsCreatingSubmission] = useState(false);
  // When the carry-forward flag is on, holds the ingestion context awaiting
  // review before a submission is created.
  const [reviewCtx, setReviewCtx] = useState(null);
  const [pendingDrop, setPendingDrop] = useState(null); // { files: File[], suggestedName: string }
  const capturedEntriesRef = useRef(null);
  const { data: ingestionConfig } = staticDataService.useConfigQuery(
    "submission_ai_ingestion_defaults"
  );
  const userConfig = useSelector(userSelectors.selectUserConfig);
  const drawerEnabled =
    userConfig.componentVisibility?.[SUBMISSION_FILE_UPLOAD_DRAWER] ?? false;
  const triageEnabled =
    userConfig.componentVisibility?.[SUBMISSION_TRIAGE] ?? false;
  const carryForwardEnabled =
    userConfig.componentVisibility?.[AI_CARRY_FORWARD_PAGE] ?? false;
  const showConfidence =
    userConfig.componentVisibility?.[AI_CARRY_FORWARD_CONFIDENCE] ?? false;
  const aiFileSelectionLabelsEnabled = staticDataService.useFeatureFlag(
    SHOW_AI_FILE_SELECTION_LABELS,
    { default: false }
  );
  const {
    data: ingestionData,
    refetch: refetchIngestionData,
  } = useGetAvailableIngestionDataQuery(undefined, {
    pollingInterval: 30000,
    skip: !drawerEnabled,
  });
  const currencyCodesList = useSelector(selectCurrencyCodes);
  const industryClassChoices = useSelector(
    (state) => state.temp.exposureMetrics?.industryClassChoices
  );

  const suggestionsConfig = useMemo(
    () => getSuggestionsConfig(ingestionConfig),
    [ingestionConfig]
  );

  // Fetch uploaded files for the "Uploaded Files" tab
  const {
    data: ingestionFilesData,
    isLoading: isLoadingFiles,
    refetch,
  } = useGetIngestionFilesQuery(
    { unused_only: false },
    { skip: !open || activeTab !== 1 } // Only fetch when drawer is open and on the uploaded files tab
  );

  // Refetch files and ingestion data when switching to uploaded files tab
  useEffect(() => {
    if (open && activeTab === 1) {
      refetch();
      refetchIngestionData();
    }
  }, [open, activeTab, refetch, refetchIngestionData]);

  // Pre-populate score-bar display from registry triage_summary — no API calls needed
  useEffect(() => {
    const individualFiles = ingestionFilesData?.individual_files || [];
    const folders = ingestionFilesData?.folders || [];
    const updates = {};

    individualFiles
      .filter((f) => f.triage_run_id && f.triage_summary)
      .forEach((f) => {
        if (!triageResults[f.file_id]?.report?.rules) {
          updates[f.file_id] = { report: f.triage_summary };
        }
      });

    folders
      .filter((folder) => folder.triage_run_id && folder.triage_summary)
      .forEach((folder) => {
        const files = Object.values(folder.files || {});
        const nonEmailBodyFiles = files.filter(
          (f) => !EMAIL_BODY_FILENAMES.has(f.file_name)
        );
        const repFileId = (
          nonEmailBodyFiles.find((f) => isReadableIngestionFile(f.file_name)) ||
          nonEmailBodyFiles[0] ||
          files[0]
        )?.file_id;
        if (repFileId && !triageResults[repFileId]?.report?.rules) {
          updates[repFileId] = { report: folder.triage_summary };
        }
      });

    if (Object.keys(updates).length > 0) {
      setTriageResults((prev) => ({ ...prev, ...updates }));
    }
  }, [ingestionFilesData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch full report on demand when user opens the triage dialog
  useEffect(() => {
    if (!triageOpenFileId) return;
    if (triageResults[triageOpenFileId]?.report?.rules) return; // already have full data
    dispatch(aiAgentsApi.endpoints.getTriageResult.initiate(triageOpenFileId))
      .unwrap()
      .then((res) => {
        if (res?.report) {
          setTriageResults((prev) => {
            const next = { ...prev, [triageOpenFileId]: res };
            try {
              sessionStorage.setItem("triageResults", JSON.stringify(next));
            } catch {}
            return next;
          });
        }
      })
      .catch(() => {});
  }, [triageOpenFileId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFileStatus = (fileId, status, error = null, progress = 0) => {
    setFileStatuses((prev) => ({
      ...prev,
      [fileId]: { status, error, progress },
    }));
  };

  const handleDownloadFile = useCallback(
    async (fileId, fileName) => {
      try {
        const result = await dispatch(
          filesApi.endpoints.fileUrl.initiate(fileId)
        ).unwrap();
        await triggerBlobDownload(result.url, fileName);
      } catch (error) {
        logger.error("File download error", error);
        enqueueSnackbar("Failed to download file", { variant: "error" });
      }
    },
    [dispatch, enqueueSnackbar]
  );

  const handleToggleReuse = useCallback(
    async (fileEntry) => {
      await setFileReuse({
        fileId: fileEntry.file_id,
        reuse: !fileEntry.reuse,
      }).unwrap();
      refetch();
    },
    [setFileReuse, refetch]
  );

  const handleRerun = useCallback(
    async (fileEntry) => {
      try {
        enqueueSnackbar(`Rerunning processing for ${fileEntry.file_name}...`, {
          variant: "info",
        });

        const result = await dispatch(
          filesApi.endpoints.fileUrl.initiate(
            { fileId: fileEntry.file_id, expiry: FILE_URL_EXPIRY_SECONDS },
            { forceRefetch: true }
          )
        ).unwrap();

        await rerunIngestionFile({
          fileName: fileEntry.file_name,
          fileUrl: result.url,
        }).unwrap();

        if (fileEntry.used_in_submission) {
          await setFileReuse({
            fileId: fileEntry.file_id,
            reuse: true,
          }).unwrap();
        }

        refetch();
        refetchIngestionData();
        enqueueSnackbar(
          `Successfully reran ingestion for ${fileEntry.file_name}`,
          {
            variant: "success",
          }
        );
      } catch (error) {
        logger.error("Rerun ingestion error", error);
        enqueueSnackbar(`Failed to rerun: ${error.message}`, {
          variant: "error",
        });
      }
    },
    [
      rerunIngestionFile,
      setFileReuse,
      dispatch,
      refetch,
      refetchIngestionData,
      enqueueSnackbar,
    ]
  );

  const handleAttachToSubmission = useCallback(
    async (
      fileEntry,
      submissionId,
      submissionName,
      allFiles = null,
      folderName = null
    ) => {
      try {
        const fieldUsageByFile = buildFieldUsageByFile();

        // When called from a folder context, mark all folder files as used
        const filesToMark = allFiles || [fileEntry];
        await Promise.all(
          filesToMark.map((f) =>
            markFileAsUsed({
              fileId: f.file_id,
              submissionId,
              fieldUsage: fieldUsageByFile[f.file_id] || null,
            }).unwrap()
          )
        );

        if (suggestionsConfig.ATTACH_FILES_TO_SUBMISSION) {
          const submissionResponse = await dispatch(
            submissionsService.submissionsApi.endpoints.retrieveSubmission.initiate(
              { submissionId }
            )
          ).unwrap();

          const existingFiles =
            submissionResponse?.state?.input?.attachments?.attachedFiles || [];

          // Add all file IDs (deduped) to the submission attachments
          const newFileIds = filesToMark
            .map((f) => f.file_id)
            .filter((id) => !existingFiles.includes(id));
          if (newFileIds.length > 0) {
            await patchSubmission({
              submissionId,
              patches: [
                {
                  type: "REPLACE",
                  path: "state.input.attachments.attachedFiles",
                  value: [...existingFiles, ...newFileIds],
                },
              ],
            }).unwrap();
          }
        }

        refetch();
        const displayName = folderName || fileEntry.file_name;
        enqueueSnackbar(
          `Attached ${displayName} to ${submissionName || "submission"}`,
          { variant: "success" }
        );
      } catch (error) {
        logger.error("Attach to submission error", error);
        enqueueSnackbar(`Failed to attach file: ${error.message}`, {
          variant: "error",
        });
      }
    },
    [
      markFileAsUsed,
      patchSubmission,
      refetch,
      enqueueSnackbar,
      suggestionsConfig,
      dispatch,
    ]
  );

  const findIngestionData = useCallback(
    async (id) => {
      // id is folder_id for folder submissions, file_id for standalone files —
      // both are direct keys in the results dict.
      const lookup = (data) => (data?.results || {})[id];
      const cached = lookup(ingestionData);
      if (cached) return cached;
      // Cache miss — refetch and retry once
      const { data: freshData } = await refetchIngestionData();
      return lookup(freshData);
    },
    [ingestionData, refetchIngestionData]
  );

  // Creates the submission from a resolved ingestion context. When `approvedKeys`
  // is provided (the carry-forward review path) only those fields are carried and
  // the automatic confidence gate is skipped — the reviewer has vetted them.
  const commitCreateSubmission = useCallback(
    async (ctx, approvedKeys = null, decisions = null) => {
      const { fileData, fileEntry, folderName, allFiles } = ctx;
      const displayName = folderName || fileEntry.file_name;
      try {
        setIsCreatingSubmission(true);

        enqueueSnackbar(`Creating submission from ${displayName}...`, {
          variant: "info",
        });

        const minConfidence = approvedKeys
          ? 0
          : suggestionsConfig.MIN_CONFIDENCE_THRESHOLD;

        let bestFields = await extractBestFields(fileData, minConfidence, {
          currencyCodesList,
          industryClassChoices,
          dispatch,
          claimsApi,
        });

        // Restrict to the reviewer's approved ingestion fields (backend keys →
        // the program uiKeys that extractBestFields produces).
        if (approvedKeys) {
          const approvedUiKeys = new Set(
            approvedKeys.filter((k) => SUGGESTION_FIELDS.includes(k))
          );
          bestFields = Object.fromEntries(
            Object.entries(bestFields).filter(([uiKey]) =>
              approvedUiKeys.has(uiKey)
            )
          );
          // A field can be approved yet fail extractBestFields' validators
          // (e.g. a currency not in the code list). Tell the reviewer rather
          // than silently drop it.
          const dropped = approvedKeys.filter(
            (k) => SUGGESTION_FIELDS.includes(k) && !(k in bestFields)
          );
          if (dropped.length > 0) {
            const names = dropped
              .map((k) => REVIEW_FIELD_LABELS[k] || k)
              .join(", ");
            enqueueSnackbar(
              `Couldn't carry over ${dropped.length} approved field${
                dropped.length > 1 ? "s" : ""
              }: ${names}`,
              { variant: "warning" }
            );
          }
        }

        if (Object.keys(bestFields).length === 0) {
          throw new Error(
            approvedKeys
              ? "None of the approved fields could be applied"
              : "No fields with sufficient confidence found"
          );
        }

        setFiles([]);
        setFileStatuses({});
        setActiveTab(0);
        onClose();

        // Record usage tracking for all prefilled fields
        Object.entries(bestFields).forEach(([fieldKey, value]) => {
          recordSuggestionSelection(
            fieldKey,
            value,
            fileEntry.file_id,
            fileData.run_id
          );
          recordFieldValue(fieldKey, value);
        });

        // Create new submission via the standard flow, then prefill
        const prevSubmissionId = persistenceSelectors.selectSubmissionId(
          store.getState()
        );
        await dispatch(persistenceActions.newSubmission({ navigate }));
        dispatch(
          programActions.updateProgram({
            ...bestFields,
            createdFromAI: true,
            aiUsedFields: Object.keys(bestFields),
          })
        );

        // Mark all files as used and attach them to the new submission (best-effort, non-blocking)
        const newSubmissionId = persistenceSelectors.selectSubmissionId(
          store.getState()
        );
        if (newSubmissionId && newSubmissionId !== prevSubmissionId) {
          const filesToMark = allFiles || [fileEntry];
          const fieldUsageByFile = buildFieldUsageByFile();
          Promise.all(
            filesToMark.map((f) => {
              const usage = fieldUsageByFile[f.file_id] || null;
              // Log the reviewer's approve/reject decisions alongside usage so
              // the extraction signal is captured (kept separate from — and not
              // reliant on — the model's confidence).
              const fieldUsage = decisions
                ? { ...(usage || {}), reviewed_fields: decisions }
                : usage;
              return markFileAsUsed({
                fileId: f.file_id,
                submissionId: newSubmissionId,
                fieldUsage,
              })
                .unwrap()
                .catch((e) => logger.error("Failed to mark file as used", e));
            })
          ).then(() => refetch());

          if (suggestionsConfig.ATTACH_FILES_TO_SUBMISSION) {
            const allFileIds = filesToMark.map((f) => f.file_id);
            dispatch(
              submissionsService.submissionsApi.endpoints.retrieveSubmission.initiate(
                { submissionId: newSubmissionId }
              )
            )
              .unwrap()
              .then((submissionResponse) => {
                const existingFiles =
                  submissionResponse?.state?.input?.attachments
                    ?.attachedFiles || [];
                const newIds = allFileIds.filter(
                  (id) => !existingFiles.includes(id)
                );
                if (newIds.length > 0) {
                  patchSubmission({
                    submissionId: newSubmissionId,
                    patches: [
                      {
                        type: "REPLACE",
                        path: "state.input.attachments.attachedFiles",
                        value: [...existingFiles, ...newIds],
                      },
                    ],
                  })
                    .unwrap()
                    .catch((e) =>
                      logger.error("Failed to attach files to submission", e)
                    );
                }
              })
              .catch((e) =>
                logger.error("Failed to retrieve submission for attachment", e)
              );
          }
        }

        enqueueSnackbar(
          `Successfully created submission with ${
            Object.keys(bestFields).length
          } pre-filled field${
            Object.keys(bestFields).length > 1 ? "s" : ""
          } from ${displayName}`,
          { variant: "success" }
        );
      } catch (error) {
        logger.error("Create submission error", error);
        enqueueSnackbar(
          `Failed to create submission from ${displayName}: ${error.message}`,
          { variant: "error" }
        );
      } finally {
        setIsCreatingSubmission(false);
      }
    },
    [
      suggestionsConfig,
      currencyCodesList,
      industryClassChoices,
      dispatch,
      navigate,
      enqueueSnackbar,
      onClose,
      store,
      markFileAsUsed,
      patchSubmission,
      refetch,
    ]
  );

  // Resolves the ingestion data, then either opens the review page (flag on) or
  // creates the submission directly (flag off) — preserving the original flow.
  const handleCreateSubmission = useCallback(
    async (fileEntry, folderName = null, allFiles = null, folderId = null) => {
      const displayName = folderName || fileEntry.file_name;
      try {
        // Disable the create control up front so a slow (cache-miss) ingestion
        // fetch can't be double-clicked into two submissions.
        setIsCreatingSubmission(true);
        const fileData = await findIngestionData(folderId || fileEntry.file_id);
        if (!fileData) {
          throw new Error("No ingestion data found for file");
        }
        const ctx = { fileData, fileEntry, folderName, allFiles, folderId };
        if (carryForwardEnabled) {
          setReviewCtx(ctx);
          setIsCreatingSubmission(false);
          return;
        }
        await commitCreateSubmission(ctx, null);
      } catch (error) {
        logger.error("Create submission error", error);
        enqueueSnackbar(
          `Failed to create submission from ${displayName}: ${error.message}`,
          { variant: "error" }
        );
        setIsCreatingSubmission(false);
      }
    },
    [
      findIngestionData,
      carryForwardEnabled,
      commitCreateSubmission,
      enqueueSnackbar,
    ]
  );

  // Download a field's source document from the review page. Source citations
  // only carry a filename, so resolve it to a file_id from the already-loaded
  // ingestion files listing.
  const downloadSource = useCallback(
    async (loc) => {
      let fileId = loc?.file_id;
      if (!fileId && loc?.filename) {
        const allFiles = [
          ...(ingestionFilesData?.individual_files || []),
          ...(ingestionFilesData?.folders || []).flatMap((f) =>
            Object.values(f.files || {})
          ),
        ];
        fileId = matchFileByName(allFiles, loc.filename)?.file_id;
      }
      if (!fileId) {
        enqueueSnackbar(
          `Source file "${loc?.filename}" isn't available to download`,
          {
            variant: "warning",
          }
        );
        return;
      }
      try {
        await downloadIngestionSource(fileId, loc.filename, ingestionFileUrl);
      } catch (e) {
        enqueueSnackbar(`Couldn't download ${loc.filename}`, {
          variant: "error",
        });
      }
    },
    [ingestionFilesData, enqueueSnackbar, ingestionFileUrl]
  );

  const handleRunTriage = useCallback(
    async (fileEntry) => {
      const ruleset =
        (selectedRulesetId
          ? triageRulesets.find((r) => r.id === selectedRulesetId)
          : defaultTriageRuleset) || triageRulesets[0];

      if (!ruleset) {
        enqueueSnackbar("No triage ruleset configured", { variant: "warning" });
        return;
      }

      setTriageErrors((prev) => {
        const next = { ...prev };
        delete next[fileEntry.file_id];
        return next;
      });

      try {
        setTriageLoadingIds((prev) => new Set([...prev, fileEntry.file_id]));

        const fileUrlResult = await dispatch(
          filesApi.endpoints.fileUrl.initiate(
            { fileId: fileEntry.file_id, expiry: FILE_URL_EXPIRY_SECONDS },
            { forceRefetch: true }
          )
        ).unwrap();

        const result = await runSubmissionTriage({
          files: [
            {
              filename: fileEntry.file_name,
              file_url: fileUrlResult.url,
              file_id: fileEntry.file_id,
            },
          ],
          ruleset,
        }).unwrap();

        setTriageResults((prev) => {
          const next = { ...prev, [fileEntry.file_id]: result };
          try {
            sessionStorage.setItem("triageResults", JSON.stringify(next));
          } catch {}
          return next;
        });

        enqueueSnackbar(
          <span>
            {"Triage complete — "}
            <span
              role={"button"}
              tabIndex={0}
              onClick={() => setTriageOpenFileId(fileEntry.file_id)}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
                fontWeight: 700,
              }}
            >
              {"view report"}
            </span>
          </span>,
          { variant: "info" }
        );
      } catch (error) {
        const isTimeout =
          error?.status === "TIMEOUT_ERROR" ||
          error?.message?.toLowerCase().includes("timeout") ||
          error?.name === "AbortError";
        const message = isTimeout
          ? "Triage timed out — retry to fetch from cache"
          : `Triage failed: ${error.message}`;
        logger.error("Triage error", error);
        enqueueSnackbar(message, { variant: isTimeout ? "warning" : "error" });
        setTriageErrors((prev) => ({
          ...prev,
          [fileEntry.file_id]: isTimeout ? "timeout" : error.message,
        }));
      } finally {
        setTriageLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(fileEntry.file_id);
          return next;
        });
      }
    },
    [
      selectedRulesetId,
      triageRulesets,
      defaultTriageRuleset,
      dispatch,
      runSubmissionTriage,
      enqueueSnackbar,
    ]
  );

  const processFiles = useCallback(
    async (acceptedFiles, folderContext = null) => {
      // Filter out unsupported file types and warn the user for each one.
      const filteredFiles = acceptedFiles.filter((file) => {
        const ext = file.name.split(".").pop().toLowerCase();
        if (SUPPORTED_EXTENSIONS.has(ext)) return true;
        enqueueSnackbar(
          `${
            file.name
          } is not supported and will be ignored. Supported types: ${[
            ...SUPPORTED_EXTENSIONS,
          ]
            .map((e) => e.toUpperCase())
            .join(", ")}`,
          { variant: "warning" }
        );
        return false;
      });
      if (filteredFiles.length === 0) return;

      // ZIP files without an existing folder context go through the project naming dialog,
      // same as a single folder drop, so the user can rename before upload.
      const filesToProcess = (() => {
        if (folderContext) return filteredFiles;
        const zips = filteredFiles.filter(
          (f) => f.name.split(".").pop().toLowerCase() === "zip"
        );
        const nonZips = filteredFiles.filter(
          (f) => f.name.split(".").pop().toLowerCase() !== "zip"
        );
        if (zips.length === 1) {
          setPendingDrop({
            files: zips,
            suggestedName: zips[0].name.replace(/\.zip$/i, ""),
          });
        } else {
          for (const zipFile of zips) {
            const folderName = zipFile.name.replace(/\.zip$/i, "");
            const folderId = `folder-${Date.now()}-${Math.random()
              .toString(36)
              .slice(2)}`;
            processFiles([zipFile], { folderId, folderName, fileCount: 1 });
          }
        }
        return nonZips;
      })();
      if (filesToProcess.length === 0) return;

      const newFiles = filesToProcess.map((file) => ({
        id: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        folderId: folderContext?.folderId || null,
        folderName: folderContext?.folderName || null,
      }));

      setFiles((prev) => [...prev, ...newFiles]);

      for (const { id, file } of newFiles) {
        try {
          updateFileStatus(id, "uploading", null, 0);

          let uploadProgress = 0;
          const progressInterval = setInterval(() => {
            uploadProgress = Math.min(uploadProgress + 10, 90);
            updateFileStatus(id, "uploading", null, uploadProgress);
          }, 200);

          const {
            id: fileId,
            hash: fileHash,
            size: fileSize,
          } = await createFileEntry(file, false, null, undefined, true);

          clearInterval(progressInterval);
          updateFileStatus(id, "uploading", null, 100);

          await new Promise((resolve) => setTimeout(resolve, 300));

          updateFileStatus(id, "processing");

          // Always forceRefetch so we never register a cached (expired) presigned URL
          const fileUrlResult = await dispatch(
            filesApi.endpoints.fileUrl.initiate(
              { fileId, expiry: FILE_URL_EXPIRY_SECONDS },
              { forceRefetch: true }
            )
          ).unwrap();
          const fileUrl = fileUrlResult.url;

          // Register ingestion file using RTK Query.
          // folderFileCount uses newFiles.length (already filtered) so the backend
          // never waits for unsupported files that were silently excluded.
          const result = await registerIngestionFile({
            fileId,
            fileName: file.name,
            fileHash,
            fileSize,
            fileUrl,
            ...(folderContext?.folderId && {
              folderId: folderContext.folderId,
            }),
            ...(folderContext?.folderName && {
              folderName: folderContext.folderName,
            }),
            ...(folderContext?.folderId && {
              folderFileCount: newFiles.length,
            }),
          }).unwrap();

          if (result.success) {
            updateFileStatus(id, "success");
            enqueueSnackbar(`Successfully processed ${file.name}`, {
              variant: "success",
            });
          } else {
            updateFileStatus(id, "error", result.error || "Processing failed");
            enqueueSnackbar(`Failed to process ${file.name}`, {
              variant: "error",
            });
          }
        } catch (error) {
          logger.error("File upload error", error);
          updateFileStatus(id, "error", error.message);
          enqueueSnackbar(`Error uploading ${file.name}: ${error.message}`, {
            variant: "error",
          });
        }
      }
    },
    [enqueueSnackbar, registerIngestionFile, dispatch]
  );

  // Intercept native drop event to capture FileSystemEntry objects before react-dropzone
  // processes them — same pattern as site/src/components/FileUpload.js
  const handleNativeDrop = useCallback((event) => {
    const items = event.dataTransfer?.items;
    if (!items || items.length === 0) return;
    const captured = captureDroppedEntries(items);
    if (captured.length > 0) capturedEntriesRef.current = captured;
  }, []);

  const handleDropzoneCallback = useCallback(
    async (acceptedFiles) => {
      if (capturedEntriesRef.current) {
        const captured = capturedEntriesRef.current;
        capturedEntriesRef.current = null;
        const filesWithPaths = await processDroppedItems(captured);
        if (filesWithPaths.length > 0) {
          // Group by root folder name derived from relativePath
          const folderGroups = {};
          const standalone = [];
          for (const {
            file,
            relativePath,
            isEmptyDirectory,
          } of filesWithPaths) {
            if (isEmptyDirectory || !file) continue;
            if (relativePath.includes("/")) {
              const root = relativePath.split("/")[0];
              if (!folderGroups[root])
                folderGroups[root] = { suggestedName: root, files: [] };
              folderGroups[root].files.push(file);
            } else {
              standalone.push(file);
            }
          }
          if (standalone.length > 0) processFiles(standalone);
          const groups = Object.values(folderGroups);
          if (groups.length === 1) {
            // Single folder drop — show naming dialog pre-filled
            setPendingDrop({
              files: groups[0].files,
              suggestedName: groups[0].suggestedName,
            });
          } else {
            // Multiple folders dropped at once — use detected names directly
            groups.forEach((g) => {
              const folderId = `folder-${Date.now()}-${Math.random()
                .toString(36)
                .slice(2)}`;
              processFiles(g.files, {
                folderId,
                folderName: g.suggestedName,
                fileCount: g.files.filter((f) =>
                  SUPPORTED_EXTENSIONS.has(
                    f.name.split(".").pop().toLowerCase()
                  )
                ).length,
              });
            });
          }
          return;
        }
      }
      // Fallback: flat file drop (no folder structure captured)
      if (!acceptedFiles.length) return;
      if (acceptedFiles.length === 1) {
        processFiles(acceptedFiles);
      } else {
        setPendingDrop({ files: acceptedFiles, suggestedName: "" });
      }
    },
    [processFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDropzoneCallback,
    multiple: true,
  });

  // Merge native drop handler into dropzone root props (same pattern as FileUpload.js)
  const dropzoneRootProps = getRootProps();
  const mergedDropzoneProps = {
    ...dropzoneRootProps,
    onDrop: (event) => {
      handleNativeDrop(event);
      if (dropzoneRootProps.onDrop) dropzoneRootProps.onDrop(event);
    },
  };

  const handleClose = () => {
    setFiles([]);
    setFileStatuses({});
    setActiveTab(0);
    onClose();
  };

  const hasActiveUploads = Object.values(fileStatuses).some((status) =>
    ["uploading", "processing"].includes(status.status)
  );

  const uploadedIndividualFiles = ingestionFilesData?.individual_files || [];
  const uploadedFolders = ingestionFilesData?.folders || [];
  // Flat list of all files (for submission ID tracking)
  const uploadedFiles = [
    ...uploadedIndividualFiles,
    ...uploadedFolders.flatMap((f) => Object.values(f.files || {})),
  ];
  const submissionIds = uploadedFiles
    .flatMap((fileEntry) => fileEntry.submission_ids || [])
    .map((submissionId) => String(submissionId));

  useEffect(() => {
    if (submissionIds.length === 0) {
      return;
    }

    let isMounted = true;
    const loadSubmissionData = async () => {
      const uniqueIds = Array.from(new Set(submissionIds));
      for (const submissionId of uniqueIds) {
        if (submissionDataById.has(submissionId)) {
          continue;
        }
        let response;
        try {
          response = await fetchSubmission({ submissionId }).unwrap();
        } catch (e) {
          logger.error("Failed to fetch submission for drawer display", {
            submissionId,
            error: e,
          });
          continue;
        }
        if (!isMounted) {
          return;
        }
        setSubmissionDataById((prev) => {
          const next = new Map(prev);
          next.set(submissionId, {
            insured: response?.insured,
            inceptionDate: response?.inception,
          });
          return next;
        });
      }
    };

    loadSubmissionData();

    return () => {
      isMounted = false;
    };
  }, [fetchSubmission, submissionIds, submissionDataById]);

  return (
    <Drawer
      anchor={"right"}
      open={open}
      onClose={hasActiveUploads ? undefined : handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 450 },
          p: 3,
        },
      }}
    >
      <Stack spacing={2} height={"100%"}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography variant={"h6"}>{"Submission Files"}</Typography>
          <IconButton
            onClick={handleClose}
            disabled={hasActiveUploads}
            size={"small"}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label={"Upload"} />
          <Tab label={"Uploaded Files"} />
        </Tabs>

        {activeTab === 0 && (
          <>
            <Box
              {...mergedDropzoneProps}
              sx={{
                border: "2px dashed",
                borderColor: isDragActive ? "primary.main" : "divider",
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                cursor: hasActiveUploads ? "not-allowed" : "pointer",
                backgroundColor: isDragActive
                  ? "action.hover"
                  : "background.default",
                transition: "all 0.2s",
                opacity: hasActiveUploads ? 0.6 : 1,
                "&:hover": {
                  borderColor: hasActiveUploads ? "divider" : "primary.main",
                  backgroundColor: hasActiveUploads
                    ? "background.default"
                    : "action.hover",
                },
              }}
            >
              <input {...getInputProps()} disabled={hasActiveUploads} />
              <UploadIcon
                sx={{
                  fontSize: 48,
                  color: isDragActive ? "primary.main" : "text.secondary",
                  mb: 2,
                }}
              />
              <Typography variant={"body1"} gutterBottom fontWeight={500}>
                {isDragActive
                  ? "Drop files or folders here"
                  : "Drag & drop files or folders, or click to select"}
              </Typography>
              <Typography variant={"caption"} color={"text.secondary"}>
                {`Supported: ${[...SUPPORTED_EXTENSIONS]
                  .map((e) => e.toUpperCase())
                  .join(", ")} · Folders supported`}
              </Typography>
            </Box>

            <FolderNamingDialog
              open={Boolean(pendingDrop)}
              suggestedName={pendingDrop?.suggestedName || ""}
              fileCount={
                pendingDrop?.files?.filter((f) =>
                  SUPPORTED_EXTENSIONS.has(
                    f.name.split(".").pop().toLowerCase()
                  )
                )?.length || 0
              }
              onConfirm={(folderName) => {
                const folderId = `folder-${Date.now()}-${Math.random()
                  .toString(36)
                  .slice(2)}`;
                processFiles(pendingDrop.files, {
                  folderId,
                  folderName,
                  fileCount: pendingDrop.files.filter((f) =>
                    SUPPORTED_EXTENSIONS.has(
                      f.name.split(".").pop().toLowerCase()
                    )
                  ).length,
                });
                setPendingDrop(null);
              }}
              onSkip={() => {
                processFiles(pendingDrop.files);
                setPendingDrop(null);
              }}
            />

            {files.length > 0 && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                  }}
                >
                  <Typography variant={"subtitle2"}>
                    {`Files (${files.length})`}
                  </Typography>
                  <Typography variant={"caption"} color={"text.secondary"}>
                    {
                      Object.values(fileStatuses).filter(
                        (s) => s.status === "success"
                      ).length
                    }{" "}
                    {"completed"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, overflow: "auto", pt: 1 }}>
                  {(() => {
                    const folderMap = new Map();
                    const standalone = [];
                    files.forEach((entry) => {
                      if (entry.folderId) {
                        if (!folderMap.has(entry.folderId)) {
                          folderMap.set(entry.folderId, {
                            folderName: entry.folderName,
                            entries: [],
                          });
                        }
                        folderMap.get(entry.folderId).entries.push(entry);
                      } else {
                        standalone.push(entry);
                      }
                    });
                    return (
                      <>
                        {Array.from(folderMap.entries()).map(
                          ([folderId, { folderName, entries }]) => {
                            const allDone = entries.every(
                              (e) => fileStatuses[e.id]?.status === "success"
                            );
                            const anyError = entries.some(
                              (e) => fileStatuses[e.id]?.status === "error"
                            );
                            const statusColor = anyError
                              ? "error"
                              : allDone
                              ? "success"
                              : "info";
                            const statusLabel = anyError
                              ? "Has errors"
                              : allDone
                              ? "Complete"
                              : "Uploading";
                            return (
                              <UploadFolderGroup
                                key={folderId}
                                folderName={folderName}
                                entries={entries}
                                fileStatuses={fileStatuses}
                                statusColor={statusColor}
                                statusLabel={statusLabel}
                                allDone={allDone}
                              />
                            );
                          }
                        )}
                        {standalone.map(({ id, file }) => (
                          <FileListItem
                            key={id}
                            file={file}
                            status={fileStatuses[id]?.status || "pending"}
                            error={fileStatuses[id]?.error}
                            progress={fileStatuses[id]?.progress || 0}
                          />
                        ))}
                      </>
                    );
                  })()}
                </Box>
              </>
            )}

            {files.length > 0 && (
              <Stack
                spacing={1.5}
                sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider" }}
              >
                {hasActiveUploads && (
                  <Typography
                    variant={"caption"}
                    color={"text.secondary"}
                    textAlign={"center"}
                  >
                    {"Uploading in progress... Please wait"}
                  </Typography>
                )}
                {!hasActiveUploads && (
                  <Stack direction={"row"} spacing={1}>
                    <Box flex={1}>
                      <Button
                        color={"secondary"}
                        onClick={() => {
                          setFiles([]);
                          setFileStatuses({});
                        }}
                        sx={{ width: "100%" }}
                      >
                        {"Clear All"}
                      </Button>
                    </Box>
                    <Box flex={1}>
                      <Button onClick={handleClose} sx={{ width: "100%" }}>
                        {"Done"}
                      </Button>
                    </Box>
                  </Stack>
                )}
              </Stack>
            )}
          </>
        )}

        {activeTab === 1 && (
          <>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant={"subtitle2"}>
                {`All Files (${
                  uploadedIndividualFiles.length + uploadedFolders.length
                })`}
              </Typography>
              <Button
                size={"small"}
                onClick={() => refetch()}
                disabled={isLoadingFiles}
              >
                {"Refresh"}
              </Button>
            </Stack>

            {triageEnabled && triageRulesets.length > 0 && (
              <FormControl size={"small"} fullWidth>
                <InputLabel>{"Triage Ruleset"}</InputLabel>
                <Select
                  label={"Triage Ruleset"}
                  value={selectedRulesetId ?? defaultTriageRuleset?.id ?? ""}
                  onChange={(e) => setSelectedRulesetId(e.target.value)}
                >
                  {triageRulesets.map((r, idx) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.name || `Ruleset ${idx + 1}`}
                      {r.isDefault && (
                        <Typography
                          variant={"caption"}
                          color={"text.secondary"}
                          sx={{ ml: 1 }}
                        >
                          {"(default)"}
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {isLoadingFiles ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <LinearProgress sx={{ width: "100%" }} />
              </Box>
            ) : uploadedIndividualFiles.length === 0 &&
              uploadedFolders.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                <Typography variant={"body2"}>
                  {"No files uploaded yet"}
                </Typography>
              </Box>
            ) : (
              <List sx={{ flex: 1, overflow: "auto", pt: 1 }}>
                {(() => {
                  const sharedProps = {
                    submissionDataById,
                    onToggleReuse: handleToggleReuse,
                    isReuseUpdating,
                    onCreateSubmission: handleCreateSubmission,
                    isCreatingSubmission,
                    suggestionsConfig,
                    onDownloadFile: handleDownloadFile,
                    onAttachToSubmission: handleAttachToSubmission,
                    onRerun: (fe) => handleRerun(fe),
                    onRunTriage: handleRunTriage,
                    triageEnabled,
                  };
                  return (
                    <>
                      {/* Folders — structure comes directly from API */}
                      {uploadedFolders.map((folder) => {
                        const group = {
                          folder_id: folder.folder_id,
                          folder_name: folder.folder_name,
                          files: Object.values(folder.files || {}),
                        };
                        const folderIngestion = aiFileSelectionLabelsEnabled
                          ? ingestionData?.results?.[folder.folder_id]
                          : null;
                        return (
                          <FolderGroup
                            key={folder.folder_id}
                            group={group}
                            source={folder.source}
                            usedFiles={folderIngestion?.used_files ?? []}
                            skippedFiles={folderIngestion?.skipped_files ?? []}
                            {...sharedProps}
                            triageResult={(fe) => triageResults[fe.file_id]}
                            triageLoading={(fe) =>
                              triageLoadingIds.has(fe.file_id)
                            }
                            triageError={(fe) => triageErrors[fe.file_id]}
                            triageDialogOpen={(fe) =>
                              triageOpenFileId === fe.file_id
                            }
                            onOpenTriageDialog={(fe, open = true) =>
                              setTriageOpenFileId(open ? fe.file_id : null)
                            }
                          />
                        );
                      })}
                      {/* Standalone files */}
                      {uploadedIndividualFiles.map((fileEntry) => (
                        <UploadedFileItem
                          key={fileEntry.file_id}
                          fileEntry={fileEntry}
                          {...sharedProps}
                          triageResult={triageResults[fileEntry.file_id]}
                          triageLoading={triageLoadingIds.has(
                            fileEntry.file_id
                          )}
                          triageError={triageErrors[fileEntry.file_id]}
                          triageEnabled={triageEnabled}
                          triageDialogOpen={
                            triageOpenFileId === fileEntry.file_id
                          }
                          onOpenTriageDialog={(open = true) =>
                            setTriageOpenFileId(open ? fileEntry.file_id : null)
                          }
                          onRerun={() => handleRerun(fileEntry)}
                        />
                      ))}
                    </>
                  );
                })()}
              </List>
            )}
          </>
        )}
      </Stack>

      {/* ── AI carry-forward review page ─────────────────────────────── */}
      <SubmissionReviewDialog
        open={Boolean(reviewCtx)}
        title={
          reviewCtx?.fileData?.insured?.value ||
          reviewCtx?.folderName ||
          reviewCtx?.fileEntry?.file_name
        }
        ingestionFields={reviewCtx?.fileData}
        showConfidence={showConfidence}
        onDownloadSource={downloadSource}
        onClose={() => setReviewCtx(null)}
        onConfirm={(approvedKeys, decisions) => {
          const ctx = reviewCtx;
          setReviewCtx(null);
          commitCreateSubmission(ctx, approvedKeys, decisions);
        }}
      />
    </Drawer>
  );
};

export default SubmissionFileUploadDrawer;
